import {
    ColumnAttributes,
    DatasetGeometryPoint,
    FilterItem,
    LogicOperator,
} from "@biggeo/bg-server-lib/studio";
import { Box, IFilterSearchPaginate } from "@biggeo/bg-ui/lab";
import * as A from "fp-ts/lib/Array";
import * as O from "fp-ts/lib/Option";
import { pipe } from "fp-ts/lib/function";
import includes from "lodash/includes";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";

import { ColorSwatchOption } from "../../../common/components/ColorSwatchSelector";
import { getLinearGradient } from "../../../common/utils/gradient";
import { DataRspSuccess, SchemaRow } from "../../../velocity/aurora/map";
import { MapContextDatasetFilter } from "../../mapbox/context/types";
import { DEFAULT_SHAPE_COLOR } from "../../mapbox/hooks/style-hooks";
import { DataRspRows } from "../../utils/type";
import { DatasetTableTab } from "../containers/DatasetTableContainer";
import { DEFAULT_LOGIC_OPERATOR, getTableColumns } from "../utils/utils";
import DatasetTableView from "./DatasetTableView";

export type DatasetTableProps = {
    readonly type: "data" | "filteredData";
    readonly view: DatasetTableTab;
    readonly setView: (v: DatasetTableTab) => void;
    readonly columns: SchemaRow[];
    readonly data: DataRspSuccess;
    readonly loading: boolean;
    readonly tableId: string;
    readonly dataPaginationProps: IFilterSearchPaginate;
    readonly heatmap?: ColorSwatchOption;
    readonly initialFilters?: MapContextDatasetFilter;
    readonly updateDataset?: (
        i: Partial<{
            filters: FilterItem[];
            logicOperator: LogicOperator;
            selectedRows: Omit<DatasetGeometryPoint, "fkDataSourceId">[];
            pinnedRows: Omit<DatasetGeometryPoint, "fkDataSourceId">[];
        }>
    ) => void;
    readonly onGoToClick: (rows: DataRspRows) => void;
    readonly isGoToFieldHidden: boolean;
    readonly selectedRows: DataRspRows;
    readonly pinnedRows: DataRspRows;
    readonly isFocused: boolean;
};

export const DatasetTable = ({
    type,
    columns: c,
    data,
    loading,
    dataPaginationProps,
    heatmap,
    initialFilters,
    updateDataset,
    onGoToClick,
    view,
    setView,
    isGoToFieldHidden,
    tableId,
    selectedRows,
    pinnedRows,
}: DatasetTableProps) => {
    const columns = getTableColumns(c);

    const gradient = heatmap ? getLinearGradient(heatmap) : undefined;

    const onPinnedRowsChange = (i: {
        action: "pin" | "unpin";
        rows: DataRspRows;
    }) => {
        const { action, rows } = i;

        if (isEqual(action, "pin")) {
            updateDataset?.({
                pinnedRows: pipe(
                    pinnedRows,
                    A.concat(rows),
                    A.map((row) => ({
                        tableId,
                        row: JSON.stringify(row),
                    }))
                ),
            });
        }

        if (isEqual(action, "unpin")) {
            updateDataset?.({
                pinnedRows: pipe(
                    pinnedRows,
                    A.filter((r) => !includes(rows, r)),
                    A.map((row) => ({
                        tableId,
                        row: JSON.stringify(row),
                    }))
                ),
            });
        }
    };

    const onSelectedRowsChange = (rows: DataRspRows) => {
        updateDataset?.({
            selectedRows: pipe(
                rows,
                A.map((row) => ({
                    tableId,
                    row: JSON.stringify(row),
                }))
            ),
        });
    };

    const onApplyFilter = (f: FilterItem[]) => {
        updateDataset?.({ filters: f });
    };

    const onOperatorChange = (operator: LogicOperator) => {
        updateDataset?.({ logicOperator: operator });
    };

    const getColumnAttributes = (
        column: string
    ): ColumnAttributes | undefined => {
        return pipe(
            c,
            A.findFirst((i) => i.columnName === column),
            O.fold(
                () => undefined,
                (schema) => schema.attributes || undefined
            )
        );
    };

    return (
        <>
            <Box
                sx={{
                    flexShrink: 0,
                    height: 2,
                    width: "100%",
                    background: gradient
                        ? `${gradient}`
                        : `linear-gradient(90deg, #EBEFFE 0%, ${DEFAULT_SHAPE_COLOR} 100%)`,
                }}
            />
            <DatasetTableView
                type={type}
                tableId={tableId}
                view={view}
                setView={setView}
                initialValues={
                    initialFilters || {
                        logicOperator: DEFAULT_LOGIC_OPERATOR,
                        filters: [],
                    }
                }
                data={data}
                isGoToFieldHidden={isGoToFieldHidden}
                selectedRows={selectedRows}
                onSelectedRowsChange={onSelectedRowsChange}
                loading={loading}
                dataPaginationProps={dataPaginationProps}
                getColumnAttributes={getColumnAttributes}
                columns={isEmpty(c) ? [] : columns}
                onApplyFilter={onApplyFilter}
                onGoToClick={onGoToClick}
                onOperatorChange={onOperatorChange}
                pinnedRows={pinnedRows}
                onPinnedRowsChange={onPinnedRowsChange}
            />
        </>
    );
};
